import { tracker } from '../../utils/core';
import type RootState from '../../utils/redux-store/rootState';
import type { AUTH_REDUCER_KEY } from '../auth/authTypes';
import { institutionLoginsSelectors } from '../institution-logins';
import type { INSTITUTION_LOGINS_REDUCER_KEY, InstitutionLogin } from '../institution-logins/institutionLoginsTypes';
import { getAggregatorFromChannel } from '../institutions/institutionsUtils';
import { getFiMigrationEligibleLogins, getFiMigrationSessionKey } from './fiMigrationSelectors';
import type {
  FiMigrationPromptAction,
  FiMigrationPromptContext,
  FiMigrationSaveCounts,
  FiMigrationType,
} from './fiMigrationTypes';
import { getFiMigrationProvenance, isForcedFiMigration, isOptionalFiMigration } from './fiMigrationUtils';

let sessionKey: string | null = null;
const eligibleLoginIds = new Set<string>();
const shownPrompts = new Set<string>();
let eligibleCount: number | null = null;
export const resetFiMigrationSession = (): void => {
  sessionKey = null;
  eligibleLoginIds.clear();
  shownPrompts.clear();
  eligibleCount = null;
};

const setFiMigrationSession = (key: string | null): void => {
  if (sessionKey !== key) {
    resetFiMigrationSession();
    sessionKey = key;
  }
};

export const trackFiMigrationPrompt = (
  login: InstitutionLogin,
  action: FiMigrationPromptAction,
  {
    fi_migration_type: actionMigrationType,
    ...context
  }: FiMigrationPromptContext & {
    fi_migration_type?: Exclude<FiMigrationType, 'unknown'>;
  },
): void => {
  if (!sessionKey || !tracker.userId) {
    return;
  }
  // Forced and optional migrations share the intro, which passes its action's type because an account alone can
  // make it forced. Other prompts are labelled from the login.
  const migrationType =
    actionMigrationType ??
    (isForcedFiMigration(login) ? 'forced' : isOptionalFiMigration(login) ? 'optional' : 'unknown');
  const promptKey = JSON.stringify([login.id, context.surface, migrationType]);
  if (action === 'shown') {
    if (shownPrompts.has(promptKey)) {
      return;
    }
    shownPrompts.add(promptKey);
  }
  tracker.track(tracker.events.fiMigrationPrompt, {
    ...getFiMigrationProvenance(login),
    ...context,
    action,
    fi_migration_type: migrationType,
  });
};

const getMigrationType = (toChannel: unknown): 'plaid' | 'other' | 'unknown' => {
  const aggregator =
    typeof toChannel === 'string' && toChannel !== 'unknown' ? getAggregatorFromChannel(toChannel) : '';
  if (!aggregator) {
    return 'unknown';
  }
  return aggregator === 'PLAID' ? 'plaid' : 'other';
};

// Call only after QCS acknowledges the account save; background refresh can still be running.
export const trackFiMigrationSaveSuccess = (
  trackingProperties: Record<string, unknown>,
  counts: FiMigrationSaveCounts,
): void => {
  // Ignored accounts are saved too, so a migration needs at least one kept account to count.
  if (trackingProperties.mode !== 'MIGRATE-INSTITUTION-LOGIN' || counts.accounts_added + counts.accounts_linked === 0) {
    return;
  }
  tracker.track(tracker.events.fiMigrationSaveSuccess, {
    ...trackingProperties,
    ...counts,
    migration_type: getMigrationType(trackingProperties.to_channel),
  });
};

const trackFiMigrationEligibility = (logins: readonly InstitutionLogin[]): void => {
  if (!sessionKey || !tracker.userId) {
    return;
  }
  const uniqueLogins = new Map(logins.map((login) => [login.id, login]));
  uniqueLogins.forEach((login) => {
    if (eligibleLoginIds.has(login.id)) {
      return;
    }
    eligibleLoginIds.add(login.id);
    tracker.track(tracker.events.fiMigrationEligibility, {
      ...getFiMigrationProvenance(login),
      action: 'eligible',
      institution_login_id: login.id,
    });
  });
  if (eligibleCount !== uniqueLogins.size) {
    // A zero is meaningful after positive eligibility, but not for every unaffected session.
    const shouldUpdate = eligibleCount !== null || uniqueLogins.size > 0;
    eligibleCount = uniqueLogins.size;
    if (shouldUpdate) {
      tracker.updateProfile({ fi_migration_eligible_count: eligibleCount });
    }
  }
};

export const observeFiMigrationEligibility = (
  state: Pick<RootState, typeof AUTH_REDUCER_KEY | typeof INSTITUTION_LOGINS_REDUCER_KEY>,
): void => {
  const key = getFiMigrationSessionKey(state);
  setFiMigrationSession(key);
  if (key && institutionLoginsSelectors.getLastSyncDate(state)) {
    trackFiMigrationEligibility(getFiMigrationEligibleLogins(state));
  }
};
